import React from 'react';
import PropTypes from 'prop-types';

/**
 * Material Content Renderer
 * Renders different types of training material content
 */
const MaterialContent = ({ content }) => {
  // Handle null or undefined
  if (!content) {
    return <p className="text-gray-500">Нет содержимого</p>;
  }

  // If content is a string, render as-is
  if (typeof content === 'string') {
    return <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">{content}</div>;
  }

  // If content is an object, render based on type
  const { type, items, steps, sections, text, title, description, intro, conclusion } = content;

  // Type: list
  if (type === 'list' && items) {
    return (
      <div className="space-y-6">
        {/* Intro text */}
        {intro && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <p className="text-gray-700">{intro}</p>
          </div>
        )}
        {items.map((item, index) => (
          <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
            {/* Header with number */}
            {item.number && (
              <div className="text-sm font-semibold text-blue-600 mb-1">
                {item.error ? `Ошибка #${item.number}` : item.law ? `Закон #${item.number}` : `#${item.number}`}
              </div>
            )}

            {/* Main title - error, law, or title */}
            {item.error && (
              <h3 className="text-lg font-bold text-red-700 mb-2">{item.error}</h3>
            )}
            {item.law && (
              <h3 className="text-lg font-bold text-gray-800 mb-2">{item.law}</h3>
            )}
            {item.title && !item.error && !item.law && (
              <h3 className="text-lg font-bold text-gray-800 mb-2">{item.title}</h3>
            )}

            {/* Main text or description */}
            {item.text && (
              <p className="text-gray-700 mb-2 font-medium">{item.text}</p>
            )}
            {item.description && (
              <p className="text-gray-700 mb-2">{item.description}</p>
            )}

            {/* Why it's bad (for errors) */}
            {item.why_bad && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-red-800 mb-1">Почему это плохо:</p>
                <p className="text-sm text-gray-700">{item.why_bad}</p>
              </div>
            )}

            {/* Bad example */}
            {item.bad_example && (
              <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-orange-800 mb-1">❌ Плохой пример:</p>
                <p className="text-sm text-gray-700 italic">"{item.bad_example}"</p>
              </div>
            )}

            {/* Good example */}
            {item.good_example && (
              <div className="bg-green-50 border border-green-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-green-800 mb-1">✅ Правильный пример:</p>
                <p className="text-sm text-gray-700 italic">"{item.good_example}"</p>
              </div>
            )}

            {/* Impact */}
            {item.impact && (
              <div className="bg-purple-50 border border-purple-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-purple-800 mb-1">Влияние:</p>
                <p className="text-sm text-gray-700">{item.impact}</p>
              </div>
            )}

            {/* Details */}
            {item.details && (
              <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-2">
                <p className="text-sm text-gray-700">{item.details}</p>
              </div>
            )}

            {/* How to use (for laws/techniques) */}
            {item.how_to_use && (
              <div className="bg-green-50 border border-green-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-green-800 mb-1">Как использовать:</p>
                <p className="text-sm text-gray-700">{item.how_to_use}</p>
              </div>
            )}

            {/* Generic example (fallback) */}
            {item.example && !item.bad_example && !item.good_example && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-sm font-semibold text-yellow-800 mb-1">Пример:</p>
                <p className="text-sm text-gray-700 italic">{item.example}</p>
              </div>
            )}

            {/* Additional content */}
            {item.content && (
              <p className="text-gray-700 mt-2">{item.content}</p>
            )}

            {/* Approach field (for LPR types) */}
            {item.approach && (
              <div className="bg-indigo-50 border border-indigo-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-indigo-800 mb-1">Подход:</p>
                <p className="text-sm text-gray-700">{item.approach}</p>
              </div>
            )}

            {/* Timing and Cost (for services) */}
            {item.timing && (
              <div className="text-sm text-gray-600 mb-1">
                <span className="font-semibold">Сроки:</span> {item.timing}
              </div>
            )}
            {item.cost && (
              <div className="text-sm text-gray-600 mb-1">
                <span className="font-semibold">Стоимость:</span> {item.cost}
              </div>
            )}
            {item.when_applicable && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-blue-800 mb-1">Когда применимо:</p>
                <p className="text-sm text-gray-700">{item.when_applicable}</p>
              </div>
            )}

            {/* How to identify (for psychology) */}
            {item.how_to_identify && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-yellow-800 mb-1">Как определить:</p>
                <p className="text-sm text-gray-700">{item.how_to_identify}</p>
              </div>
            )}

            {/* Array fields rendering */}
            {item.triggers && Array.isArray(item.triggers) && item.triggers.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-orange-800 mb-1">Триггеры:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {item.triggers.map((trigger, idx) => (
                    <li key={idx}>{trigger}</li>
                  ))}
                </ul>
              </div>
            )}

            {item.phrases && Array.isArray(item.phrases) && item.phrases.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-green-800 mb-1">Ключевые фразы:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {item.phrases.map((phrase, idx) => (
                    <li key={idx}>{phrase}</li>
                  ))}
                </ul>
              </div>
            )}

            {item.characteristics && Array.isArray(item.characteristics) && item.characteristics.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-purple-800 mb-1">Характеристики:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {item.characteristics.map((char, idx) => (
                    <li key={idx}>{char}</li>
                  ))}
                </ul>
              </div>
            )}

            {item.key_phrases && Array.isArray(item.key_phrases) && item.key_phrases.length > 0 && (
              <div className="bg-teal-50 border border-teal-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-teal-800 mb-1">Ключевые фразы для работы:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {item.key_phrases.map((phrase, idx) => (
                    <li key={idx}>{phrase}</li>
                  ))}
                </ul>
              </div>
            )}

            {item.what_not_to_say && Array.isArray(item.what_not_to_say) && item.what_not_to_say.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-red-800 mb-1">❌ Чего НЕ говорить:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {item.what_not_to_say.map((phrase, idx) => (
                    <li key={idx}>{phrase}</li>
                  ))}
                </ul>
              </div>
            )}

            {item.what_includes && Array.isArray(item.what_includes) && item.what_includes.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-blue-800 mb-1">Что входит:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {item.what_includes.map((incl, idx) => (
                    <li key={idx}>{incl}</li>
                  ))}
                </ul>
              </div>
            )}

            {item.key_benefits && Array.isArray(item.key_benefits) && item.key_benefits.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded p-3 mb-2">
                <p className="text-sm font-semibold text-green-800 mb-1">✅ Ключевые преимущества:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {item.key_benefits.map((benefit, idx) => (
                    <li key={idx}>{benefit}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
        {/* Conclusion text */}
        {conclusion && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
            <p className="text-gray-700 font-medium">{conclusion}</p>
          </div>
        )}
      </div>
    );
  }

  // Type: sections
  if (type === 'sections' && sections) {
    return (
      <div className="space-y-6">
        {/* Intro text */}
        {intro && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <p className="text-gray-700">{intro}</p>
          </div>
        )}
        {sections.map((section, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-4">
            {section.title && (
              <h3 className="text-xl font-bold text-gray-800 mb-3">{section.title}</h3>
            )}
            {section.content && (
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap mb-3">
                {section.content}
              </div>
            )}
            {section.points && (
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                {section.points.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
            )}

            {/* Documents list */}
            {section.documents && Array.isArray(section.documents) && (
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm font-semibold text-blue-800 mb-1">📄 Необходимые документы:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {section.documents.map((doc, idx) => (
                    <li key={idx}>{doc}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Additional info by category */}
            {section.additional && typeof section.additional === 'object' && (
              <div className="mt-3 bg-gray-100 border border-gray-200 rounded p-3">
                <p className="text-sm font-semibold text-gray-800 mb-2">Дополнительно по типам:</p>
                {Object.entries(section.additional).map(([key, value], idx) => (
                  <div key={idx} className="text-sm text-gray-700 mb-1">
                    <span className="font-medium">{key}:</span> {value}
                  </div>
                ))}
              </div>
            )}

            {/* Important notice */}
            {section.important && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded p-3">
                <p className="text-sm font-semibold text-red-800">⚠️ {section.important}</p>
              </div>
            )}

            {/* Problems list */}
            {section.problems && Array.isArray(section.problems) && (
              <div className="mt-3 bg-orange-50 border border-orange-200 rounded p-3">
                <p className="text-sm font-semibold text-orange-800 mb-1">Типичные проблемы:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {section.problems.map((problem, idx) => (
                    <li key={idx}>{problem}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Services list */}
            {section.services && Array.isArray(section.services) && (
              <div className="mt-3 bg-green-50 border border-green-200 rounded p-3">
                <p className="text-sm font-semibold text-green-800 mb-1">Наши услуги:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {section.services.map((service, idx) => (
                    <li key={idx}>{service}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Subsections */}
            {section.subsections && Array.isArray(section.subsections) && (
              <div className="mt-4 space-y-3 ml-4 border-l-2 border-gray-300 pl-4">
                {section.subsections.map((subsection, subIdx) => (
                  <div key={subIdx} className="bg-white rounded p-3">
                    {subsection.subtitle && (
                      <h4 className="font-semibold text-gray-800 mb-2">{subsection.subtitle}</h4>
                    )}
                    {subsection.problems && Array.isArray(subsection.problems) && (
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-orange-700 mb-1">Проблемы:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                          {subsection.problems.map((p, pIdx) => (
                            <li key={pIdx}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {subsection.services && Array.isArray(subsection.services) && (
                      <div>
                        <p className="text-xs font-semibold text-green-700 mb-1">Решения:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                          {subsection.services.map((s, sIdx) => (
                            <li key={sIdx}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {section.examples && (
              <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-sm font-semibold text-yellow-800 mb-1">Примеры:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {section.examples.map((example, idx) => (
                    <li key={idx}>{example}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
        {/* Conclusion text */}
        {conclusion && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
            <p className="text-gray-700 font-medium">{conclusion}</p>
          </div>
        )}
      </div>
    );
  }

  // Type: text
  if (type === 'text' || text) {
    return (
      <div className="space-y-4">
        {title && <h3 className="text-xl font-bold text-gray-800">{title}</h3>}
        {description && <p className="text-gray-600">{description}</p>}
        {text && <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">{text}</div>}
      </div>
    );
  }

  // Type: steps
  const stepsData = steps || items;
  if (type === 'steps' && stepsData) {
    return (
      <div className="space-y-4">
        {/* Intro text */}
        {intro && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <p className="text-gray-700">{intro}</p>
          </div>
        )}
        {stepsData.map((step, index) => (
          <div key={index} className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold mr-3">
              {step.number || index + 1}
            </div>
            <div className="flex-1">
              {step.title && <h4 className="font-semibold text-gray-800 mb-1">{step.title}</h4>}
              {step.description && <p className="text-gray-700">{step.description}</p>}
              {step.details && (
                <div className="mt-2 bg-gray-50 border border-gray-200 rounded p-2 text-sm text-gray-600 whitespace-pre-wrap">
                  {step.details}
                </div>
              )}
              {step.example && (
                <p className="text-sm text-gray-600 italic mt-1">Пример: {step.example}</p>
              )}
            </div>
          </div>
        ))}
        {/* Conclusion text */}
        {conclusion && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
            <p className="text-gray-700 font-medium">{conclusion}</p>
          </div>
        )}
      </div>
    );
  }

  // Type: table (for objections, price lists, and similar)
  if (type === 'table' && content.headers && content.rows) {
    // Determine table format based on first row structure
    const firstRow = content.rows[0] || {};
    const isObjectionTable = 'objection' in firstRow;
    const isPriceTable = 'service' in firstRow || 'price' in firstRow;

    return (
      <div className="space-y-4">
        {/* Intro text */}
        {intro && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <p className="text-gray-700">{intro}</p>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                {content.headers.map((header, idx) => (
                  <th key={idx} className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-700">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {content.rows.map((row, rowIdx) => (
                <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {isObjectionTable ? (
                    <>
                      <td className="border border-gray-300 px-4 py-2 text-gray-800 font-medium">
                        {row.objection}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-gray-600 text-sm">
                        {row.reason}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-gray-700">
                        {row.answer}
                      </td>
                    </>
                  ) : isPriceTable ? (
                    <>
                      <td className="border border-gray-300 px-4 py-2 text-gray-600 text-center w-12">
                        {row.number}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-gray-800">
                        {row.service}
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-gray-700 font-medium whitespace-nowrap">
                        {row.price}
                      </td>
                    </>
                  ) : (
                    /* Generic fallback - render all values */
                    Object.values(row).map((value, cellIdx) => (
                      <td key={cellIdx} className="border border-gray-300 px-4 py-2 text-gray-700">
                        {value}
                      </td>
                    ))
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Conclusion text */}
        {conclusion && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
            <p className="text-gray-700 font-medium">{conclusion}</p>
          </div>
        )}
      </div>
    );
  }

  // Type: dialog (for scripts with conversations)
  if (type === 'dialog' && content.stages) {
    return (
      <div className="space-y-8">
        {content.stages.map((stage, stageIdx) => (
          <div key={stageIdx} className="border-l-4 border-blue-500 pl-4">
            {/* Stage header */}
            <div className="mb-4">
              <h3 className="text-xl font-bold text-gray-800 mb-1">{stage.stage}</h3>
              {stage.goal && (
                <p className="text-sm text-gray-600 italic">Цель: {stage.goal}</p>
              )}
            </div>

            {/* Dialog */}
            {stage.dialog && Array.isArray(stage.dialog) && (
              <div className="space-y-3 mb-4">
                {stage.dialog.map((exchange, exchIdx) => (
                  <div key={exchIdx} className="space-y-2">
                    {exchange.you && (
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                        <p className="text-xs font-semibold text-blue-700 mb-1">ВЫ (менеджер):</p>
                        <p className="text-gray-800">{exchange.you}</p>
                      </div>
                    )}
                    {exchange.secretary && (
                      <div className="bg-orange-50 border-l-4 border-orange-400 p-3 rounded">
                        <p className="text-xs font-semibold text-orange-700 mb-1">СЕКРЕТАРЬ:</p>
                        <p className="text-gray-800">{exchange.secretary}</p>
                      </div>
                    )}
                    {exchange.lpr && (
                      <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded">
                        <p className="text-xs font-semibold text-green-700 mb-1">ЛПР (директор):</p>
                        <p className="text-gray-800">{exchange.lpr}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Tips */}
            {stage.tips && Array.isArray(stage.tips) && stage.tips.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-sm font-semibold text-yellow-800 mb-2">💡 Советы:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {stage.tips.map((tip, tipIdx) => (
                    <li key={tipIdx}>{tip}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Type: training_module (comprehensive module content)
  if (type === 'training_module') {
    const moduleSections = content.sections || sections;
    const moduleSteps = content.steps;

    return (
      <div className="space-y-6">
        {/* Time limit warning */}
        {content.time_limit && (
          <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
            <p className="text-sm font-semibold text-orange-800">⏰ {content.time_limit}</p>
          </div>
        )}

        {/* Main rule (Module 5) */}
        {content.main_rule && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <p className="text-sm font-semibold text-red-800 mb-1">⚠️ ГЛАВНОЕ ПРАВИЛО:</p>
            <p className="text-gray-700 font-medium">{content.main_rule}</p>
          </div>
        )}

        {/* Intro (Module 5) */}
        {content.intro && !moduleSections && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
            <p className="text-gray-700">{content.intro}</p>
          </div>
        )}

        {/* Generic Items (Module 14 - rules, etc.) */}
        {content.items && Array.isArray(content.items) && (
          <div className="space-y-4">
            {content.items.map((item, idx) => (
              <div key={idx} className="bg-white border-2 border-slate-200 rounded-lg p-5 space-y-2 hover:border-slate-300 transition-colors shadow-sm">
                {item.title && (
                  <h4 className="text-lg font-bold text-slate-800 flex items-center">
                    <span className="w-2 h-6 bg-slate-500 rounded-full mr-3"></span>
                    {item.title}
                  </h4>
                )}
                {item.text && (
                  <p className="text-gray-800 font-semibold">{item.text}</p>
                )}
                {item.details && (
                  <div className="text-gray-600 text-sm leading-relaxed bg-slate-50 p-3 rounded">
                    {item.details}
                  </div>
                )}
                {item.content && (
                  <div className="text-gray-700 mt-2">
                    {item.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Techniques (Module 5 - secretary bypass techniques) */}
        {content.techniques && Array.isArray(content.techniques) && (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Техники обхода секретаря:</h3>
            {content.techniques.map((technique, idx) => (
              <div key={idx} className="bg-white border-2 border-blue-200 rounded-lg p-5 space-y-3">
                {/* Technique header */}
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold mr-4 text-lg">
                    {technique.number}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-800 mb-1">{technique.name}</h4>
                  </div>
                </div>

                {/* Approach */}
                {technique.approach && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded p-3">
                    <p className="text-sm font-semibold text-indigo-800 mb-1">Подход:</p>
                    <p className="text-gray-700">{technique.approach}</p>
                  </div>
                )}

                {/* Script */}
                {technique.script && (
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <p className="text-sm font-semibold text-green-800 mb-1">📝 Скрипт:</p>
                    <p className="text-gray-700 italic">"{technique.script}"</p>
                  </div>
                )}

                {/* Warning */}
                {technique.warning && (
                  <div className="bg-orange-50 border border-orange-200 rounded p-3">
                    <p className="text-sm font-semibold text-orange-800">⚠️ {technique.warning}</p>
                  </div>
                )}

                {/* Why it works */}
                {technique.why_works && (
                  <div className="bg-purple-50 border border-purple-200 rounded p-3">
                    <p className="text-sm font-semibold text-purple-800 mb-1">Почему работает:</p>
                    <p className="text-gray-700">{technique.why_works}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Bonus technique (Module 5) */}
        {content.bonus && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg p-5 space-y-3">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">🎁</span>
              <h4 className="text-lg font-bold text-gray-800">Бонус: {content.bonus.name}</h4>
            </div>

            {content.bonus.description && (
              <p className="text-gray-700">{content.bonus.description}</p>
            )}

            {content.bonus.script && (
              <div className="bg-white border border-yellow-300 rounded p-3">
                <p className="text-sm font-semibold text-yellow-800 mb-1">📝 Скрипт:</p>
                <p className="text-gray-700 italic">"{content.bonus.script}"</p>
              </div>
            )}

            {content.bonus.outcome && (
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-sm font-semibold text-green-800 mb-1">✅ Результат:</p>
                <p className="text-gray-700">{content.bonus.outcome}</p>
              </div>
            )}
          </div>
        )}

        {/* Arguments for secretary (Module 5) */}
        {content.arguments_for_secretary && Array.isArray(content.arguments_for_secretary) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <p className="text-sm font-semibold text-blue-800 mb-3">💬 Аргументы для секретаря:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              {content.arguments_for_secretary.map((arg, idx) => (
                <li key={idx}>{arg}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Key principle (Module 5) */}
        {content.key_principle && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-indigo-500 p-4">
            <p className="text-sm font-semibold text-indigo-800 mb-1">🔑 Ключевой принцип:</p>
            <p className="text-gray-700 font-medium">{content.key_principle}</p>
          </div>
        )}

        {/* LPR types (Module 5 - decision maker types) */}
        {content.lpr_types && Array.isArray(content.lpr_types) && (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Типы ЛПР (лиц, принимающих решения):</h3>
            {content.lpr_types.map((lprType, idx) => (
              <div key={idx} className="bg-white border-2 border-purple-200 rounded-lg p-5 space-y-3">
                {/* LPR Type header */}
                <h4 className="text-lg font-bold text-purple-800 mb-2">{lprType.type}</h4>

                {/* Context */}
                {lprType.context && (
                  <div className="bg-gray-50 border border-gray-200 rounded p-3">
                    <p className="text-sm font-semibold text-gray-800 mb-1">📋 Контекст:</p>
                    <p className="text-gray-700">{lprType.context}</p>
                  </div>
                )}

                {/* Mindset */}
                {lprType.mindset && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <p className="text-sm font-semibold text-yellow-800 mb-1">💭 Мышление:</p>
                    <p className="text-gray-700 italic">"{lprType.mindset}"</p>
                  </div>
                )}

                {/* Approach array */}
                {lprType.approach && Array.isArray(lprType.approach) && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded p-3">
                    <p className="text-sm font-semibold text-indigo-800 mb-2">Подход:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      {lprType.approach.map((item, aIdx) => (
                        <li key={aIdx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Script */}
                {lprType.script && (
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <p className="text-sm font-semibold text-green-800 mb-1">📝 Скрипт:</p>
                    <p className="text-gray-700 italic">"{lprType.script}"</p>
                  </div>
                )}

                {/* Approach by motive (for CEO/Owner) */}
                {lprType.approach_by_motive && (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-800 mb-2">🎯 Подход в зависимости от мотива:</p>
                    {Object.entries(lprType.approach_by_motive).map(([motive, motiveData], mIdx) => (
                      <div key={mIdx} className="bg-gradient-to-r from-pink-50 to-purple-50 border border-purple-300 rounded p-4">
                        <h5 className="font-bold text-purple-900 mb-2 capitalize">Мотив: {motive}</h5>

                        {motiveData.signs && (
                          <div className="mb-2">
                            <p className="text-xs font-semibold text-purple-700 mb-1">Признаки:</p>
                            <p className="text-sm text-gray-700">{motiveData.signs}</p>
                          </div>
                        )}

                        {motiveData.approach && (
                          <div className="mb-2">
                            <p className="text-xs font-semibold text-purple-700 mb-1">Подход:</p>
                            <p className="text-sm text-gray-700">{motiveData.approach}</p>
                          </div>
                        )}

                        {motiveData.script && (
                          <div className="bg-white border border-purple-200 rounded p-2">
                            <p className="text-xs font-semibold text-purple-700 mb-1">Скрипт:</p>
                            <p className="text-sm text-gray-700 italic">"{motiveData.script}"</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Golden rule (Module 5) */}
        {content.golden_rule && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-amber-400 rounded-lg p-5">
            <div className="flex items-center">
              <span className="text-3xl mr-3">👑</span>
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">ЗОЛОТОЕ ПРАВИЛО:</p>
                <p className="text-gray-800 font-bold text-lg">{content.golden_rule}</p>
              </div>
            </div>
          </div>
        )}

        {/* Important fact (Module 10) */}
        {
          content.important_fact && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-orange-500 p-4">
              <p className="text-sm font-semibold text-orange-800 mb-1">⚡ Важный факт:</p>
              <p className="text-gray-700 font-medium">{content.important_fact}</p>
            </div>
          )
        }

        {/* Categories (Module 10 - services catalog) */}
        {
          content.categories && Array.isArray(content.categories) && (
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Каталог услуг:</h3>
              {content.categories.map((category, idx) => (
                <div key={idx} className="bg-gradient-to-r from-gray-50 to-blue-50 border-l-4 border-blue-500 rounded-lg p-5 space-y-4">
                  <h4 className="text-lg font-bold text-blue-900">{category.category}</h4>

                  {category.services && Array.isArray(category.services) && (
                    <div className="space-y-3">
                      {category.services.map((service, sIdx) => (
                        <div key={sIdx} className="bg-white border border-gray-200 rounded p-4">
                          <h5 className="font-bold text-gray-800 mb-2">{service.name}</h5>

                          {service.description && (
                            <p className="text-gray-700 mb-2">{service.description}</p>
                          )}

                          {service.types_of_debt && Array.isArray(service.types_of_debt) && (
                            <div className="mb-2">
                              <p className="text-sm font-semibold text-gray-700 mb-1">Типы долга:</p>
                              <ul className="list-disc list-inside text-sm text-gray-600">
                                {service.types_of_debt.map((type, tIdx) => (
                                  <li key={tIdx}>{type}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {service.key_questions && Array.isArray(service.key_questions) && (
                            <div className="mb-2">
                              <p className="text-sm font-semibold text-blue-700 mb-1">Ключевые вопросы:</p>
                              <ul className="list-disc list-inside text-sm text-gray-600">
                                {service.key_questions.map((q, qIdx) => (
                                  <li key={qIdx}>{q}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {service.grounds && (
                            <div className="mb-2">
                              <p className="text-sm font-semibold text-gray-700 mb-1">Основания:</p>
                              <ul className="list-disc list-inside text-sm text-gray-600">
                                {service.grounds.map((g, gIdx) => (
                                  <li key={gIdx}>{g}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {service.issues && (
                            <div className="mb-2">
                              <p className="text-sm font-semibold text-gray-700 mb-1">Проблемы:</p>
                              <ul className="list-disc list-inside text-sm text-gray-600">
                                {service.issues.map((issue, iIdx) => (
                                  <li key={iIdx}>{issue}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {service.types && (
                            <div className="mb-2">
                              <p className="text-sm font-semibold text-gray-700 mb-1">Типы:</p>
                              <ul className="list-disc list-inside text-sm text-gray-600">
                                {service.types.map((type, tIdx) => (
                                  <li key={tIdx}>{type}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {service.documents && (
                            <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-2">
                              <p className="text-xs font-semibold text-blue-700 mb-1">Документы:</p>
                              <ul className="list-disc list-inside text-xs text-gray-600">
                                {service.documents.map((doc, dIdx) => (
                                  <li key={dIdx}>{doc}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {service.methods && (
                            <div className="mb-2">
                              <p className="text-sm font-semibold text-gray-700 mb-1">Методы:</p>
                              <ul className="list-disc list-inside text-sm text-gray-600">
                                {service.methods.map((method, mIdx) => (
                                  <li key={mIdx}>{method}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {service.actions && (
                            <div className="mb-2">
                              <p className="text-sm font-semibold text-gray-700 mb-1">Действия:</p>
                              <ul className="list-disc list-inside text-sm text-gray-600">
                                {service.actions.map((action, aIdx) => (
                                  <li key={aIdx}>{action}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {service.note && (
                            <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">💡 {service.note}</p>
                          )}

                          {service.warning && (
                            <p className="text-xs text-red-600 bg-red-50 p-2 rounded">⚠️ {service.warning}</p>
                          )}

                          {service.advantage && (
                            <p className="text-xs text-green-600 bg-green-50 p-2 rounded">✅ {service.advantage}</p>
                          )}

                          {service.clarify && (
                            <p className="text-xs text-gray-600 italic">{service.clarify}</p>
                          )}

                          {service.process && (
                            <p className="text-xs text-gray-600">{service.process}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        }

        {/* Principles (Module 6) */}
        {
          content.principles && Array.isArray(content.principles) && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Принципы работы:</h3>
              {content.principles.map((principle, idx) => (
                <div key={idx} className="bg-white border-2 border-indigo-200 rounded-lg p-5 space-y-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold mr-4 text-lg">
                      {principle.number}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-800 mb-1">{principle.title}</h4>
                    </div>
                  </div>

                  {principle.why && (
                    <div className="bg-purple-50 border border-purple-200 rounded p-3">
                      <p className="text-sm font-semibold text-purple-800 mb-1">Почему:</p>
                      <p className="text-gray-700">{principle.why}</p>
                    </div>
                  )}

                  {principle.how && Array.isArray(principle.how) && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <p className="text-sm font-semibold text-blue-800 mb-2">Как:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        {principle.how.map((item, hIdx) => (
                          <li key={hIdx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {principle.what_to_understand && Array.isArray(principle.what_to_understand) && (
                    <div className="bg-yellow-50 border border- yellow-200 rounded p-3">
                      <p className="text-sm font-semibold text-yellow-800 mb-2">Что понять:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        {principle.what_to_understand.map((item, wIdx) => (
                          <li key={wIdx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {principle.technique && (
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <p className="text-sm font-semibold text-green-800 mb-1">Техника:</p>
                      <p className="text-gray-700 italic">"{principle.technique}"</p>
                    </div>
                  )}

                  {principle.warning && (
                    <div className="bg-orange-50 border border-orange-200 rounded p-3">
                      <p className="text-sm font-semibold text-orange-800">⚠️ {principle.warning}</p>
                    </div>
                  )}

                  {principle.three_motives && (
                    <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-purple-300 rounded p-4">
                      <p className="text-sm font-semibold text-purple-800 mb-3">Три мотива клиента:</p>
                      {Object.entries(principle.three_motives).map(([motive, motiveData], mIdx) => (
                        <div key={mIdx} className="mb-3 last:mb-0">
                          <p className="font-bold text-purple-900 capitalize mb-1">{motive}:</p>
                          {motiveData.signs && <p className="text-sm text-gray-700 mb-1"><span className="font-semibold">Признаки:</span> {motiveData.signs}</p>}
                          {motiveData.approach && <p className="text-sm text-gray-700"><span className="font-semibold">Подход:</span> {motiveData.approach}</p>}
                        </div>
                      ))}
                    </div>
                  )}

                  {principle.consequences && (
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <p className="text-sm font-semibold text-red-800 mb-2">Последствия:</p>
                      {Object.entries(principle.consequences).map(([key, value], cIdx) => {
                        const labelMap = {
                          'if_win_argument': 'Если выиграли спор',
                          'if_lose_argument': 'Если проиграли спор'
                        };
                        return (
                          <p key={cIdx} className="text-sm text-gray-700 mb-1">
                            <span className="font-semibold">{labelMap[key] || key}:</span> {value}
                          </p>
                        );
                      })}
                    </div>
                  )}

                  {principle.example && typeof principle.example === 'object' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                      <p className="text-sm font-semibold text-yellow-800 mb-2">Пример:</p>
                      {principle.example.objection && <p className="text-sm text-gray-700 mb-1"><span className="font-semibold">Возражение:</span> "{principle.example.objection}"</p>}
                      {principle.example.response && <p className="text-sm text-gray-700"><span className="font-semibold">Ответ:</span> "{principle.example.response}"</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        }

        {/* Why questions work, Benefits, Rules (Module 7) */}
        {
          content.why_questions_work && (
            <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-5 space-y-3">
              <h4 className="text-lg font-bold text-blue-900">Почему вопросы работают:</h4>
              {content.why_questions_work.principle && <p className="text-gray-700"><span className="font-semibold">Принцип:</span> {content.why_questions_work.principle}</p>}
              {content.why_questions_work.explanation && <p className="text-gray-700">{content.why_questions_work.explanation}</p>}
              {content.why_questions_work.technique && <p className="text-gray-700 italic"><span className="font-semibold">Техника:</span> {content.why_questions_work.technique}</p>}
              {content.why_questions_work.result && <p className="text-green-700 font-medium"><span className="font-semibold">Результат:</span> {content.why_questions_work.result}</p>}
            </div>
          )
        }

        {
          content.benefits_of_questions && Array.isArray(content.benefits_of_questions) && (
            <div className="space-y-3">
              <h4 className="text-lg font-bold text-gray-800">Преимущества вопросов:</h4>
              {content.benefits_of_questions.map((benefit, idx) => (
                <div key={idx} className="bg-green-50 border border-green-200 rounded p-4">
                  <p className="font-bold text-green-800 mb-1">{benefit.benefit}</p>
                  {benefit.how && <p className="text-sm text-gray-700 mb-1">{benefit.how}</p>}
                  {benefit.note && <p className="text-xs text-green-600 italic">💡 {benefit.note}</p>}
                </div>
              ))}
            </div>
          )
        }

        {
          content.how_to_ask_questions && (
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-5 space-y-2">
              <h4 className="text-lg font-bold text-yellow-900">Как задавать вопросы:</h4>
              {content.how_to_ask_questions.preparation && <p className="text-gray-700 font-medium">{content.how_to_ask_questions.preparation}</p>}
              {content.how_to_ask_questions.steps && (
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  {content.how_to_ask_questions.steps.map((step, sIdx) => (
                    <li key={sIdx}>{step}</li>
                  ))}
                </ul>
              )}
              {content.how_to_ask_questions.bad_example && (
                <p className="text-sm text-red-600">❌ Плохо: "{content.how_to_ask_questions.bad_example}"</p>
              )}
              {content.how_to_ask_questions.good_example && (
                <p className="text-sm text-green-600">✅ Хорошо: "{content.how_to_ask_questions.good_example}"</p>
              )}
            </div>
          )
        }

        {
          content.rules && Array.isArray(content.rules) && (
            <div className="space-y-3">
              <h4 className="text-lg font-bold text-gray-800">Правила:</h4>
              {content.rules.map((rule, idx) => (
                <div key={idx} className="bg-indigo-50 border border-indigo-200 rounded p-4">
                  <p className="font-bold text-indigo-800 mb-1">{rule.rule}</p>
                  {rule.how && <p className="text-sm text-gray-700 mb-1">{rule.how}</p>}
                  {rule.script && <p className="text-sm text-green-700 italic">📝 "{rule.script}"</p>}
                </div>
              ))}
            </div>
          )
        }

        {
          content.key_insight && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-400 rounded-lg p-5">
              <p className="text-sm font-semibold text-purple-800 mb-1">💎 Ключевое понимание:</p>
              <p className="text-gray-800 font-medium text-lg">{content.key_insight}</p>
            </div>
          )
        }

        {/* Aspects (Module 7 - speech aspects) */}
        {
          content.aspects && Array.isArray(content.aspects) && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Аспекты речи:</h3>
              {content.aspects.map((aspect, idx) => (
                <div key={idx} className="bg-white border-2 border-teal-200 rounded-lg p-5 space-y-2">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold mr-4 text-lg">
                      {aspect.number}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-800 mb-1">{aspect.aspect}</h4>
                    </div>
                  </div>

                  {aspect.description && <p className="text-gray-700">{aspect.description}</p>}
                  {aspect.warning && <p className="text-sm text-orange-600 bg-orange-50 p-2 rounded">⚠️ {aspect.warning}</p>}
                  {aspect.advice && <p className="text-sm text-blue-600">{aspect.advice}</p>}
                  {aspect.problem && <p className="text-sm text-red-600">Проблема: {aspect.problem}</p>}
                  {aspect.solution && <p className="text-sm text-green-600">Решение: {aspect.solution}</p>}
                  {aspect.bad_example && <p className="text-sm text-red-600">❌ "{aspect.bad_example}"</p>}
                  {aspect.good_example && <p className="text-sm text-green-600">✅ "{aspect.good_example}"</p>}
                  {aspect.examples && <p className="text-sm text-gray-600">Примеры: {aspect.examples}</p>}
                  {aspect.result && <p className="text-sm text-purple-600 font-medium">→ {aspect.result}</p>}
                  {aspect.why && <p className="text-sm text-gray-600">Почему: {aspect.why}</p>}
                  {aspect.how && <p className="text-sm text-gray-600">Как: {aspect.how}</p>}
                </div>
              ))}
            </div>
          )
        }

        {/* Secrets (Module 8) */}
        {
          content.secrets && Array.isArray(content.secrets) && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Секреты успеха:</h3>
              {content.secrets.map((secret, idx) => (
                <div key={idx} className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-amber-300 rounded-lg p-5 space-y-2">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold mr-4 text-lg">
                      {secret.number}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-amber-900 mb-1">{secret.secret}</h4>
                    </div>
                  </div>

                  {secret.description && <p className="text-gray-700">{secret.description}</p>}
                  {secret.analogy && <p className="text-sm text-gray-600 italic">Аналогия: {secret.analogy}</p>}
                  {secret.note && <p className="text-sm text-blue-600">📝 {secret.note}</p>}
                  {secret.insight && <p className="text-sm text-purple-600">💡 {secret.insight}</p>}
                  {secret.how_to_develop && <p className="text-sm text-green-600">{secret.how_to_develop}</p>}
                  {secret.why && <p className="text-sm text-gray-600">Почему: {secret.why}</p>}
                  {secret.warning && <p className="text-sm text-orange-600">⚠️ {secret.warning}</p>}
                  {secret.advice && <p className="text-sm text-blue-600">{secret.advice}</p>}
                  {secret.requirement && <p className="text-sm text-red-600 font-medium">{secret.requirement}</p>}

                  {secret.metrics && typeof secret.metrics === 'object' && (
                    <div className="bg-white border border-gray-200 rounded p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Метрики:</p>
                      {Object.entries(secret.metrics).map(([key, value], mIdx) => (
                        <p key={mIdx} className="text-xs text-gray-600">• {key}: {value}</p>
                      ))}
                    </div>
                  )}

                  {secret.approach && Array.isArray(secret.approach) && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <p className="text-xs font-semibold text-blue-700 mb-1">Подход:</p>
                      <ul className="list-disc list-inside text-xs text-gray-600">
                        {secret.approach.map((item, aIdx) => (
                          <li key={aIdx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        }

        {/* Daily metrics (Module 12) */}
        {content.daily_metrics && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-400 rounded-lg p-5">
            <p className="text-lg font-semibold text-indigo-900 mb-3">📊 Ежедневные метрики:</p>
            <div className="space-y-2">
              {content.daily_metrics.calls_per_day && (
                <div className="bg-white rounded p-3 border border-indigo-200">
                  <p className="text-gray-800">
                    <span className="font-bold text-indigo-700">Звонков в день:</span> {content.daily_metrics.calls_per_day}
                  </p>
                </div>
              )}
              {content.daily_metrics.conversations_per_day && (
                <div className="bg-white rounded p-3 border border-indigo-200">
                  <p className="text-gray-800">
                    <span className="font-bold text-indigo-700">Разговоров с ЛПР в день:</span> {content.daily_metrics.conversations_per_day}
                  </p>
                </div>
              )}
              {content.daily_metrics.meetings_per_week && (
                <div className="bg-white rounded p-3 border border-indigo-200">
                  <p className="text-gray-800">
                    <span className="font-bold text-indigo-700">Встреч в неделю:</span> {content.daily_metrics.meetings_per_week}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Business types (Module 11) */}
        {content.business_types && Array.isArray(content.business_types) && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Типовые ситуации по видам бизнеса:</h3>
            {content.business_types.map((businessType, idx) => (
              <div key={idx} className="bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-cyan-500 rounded-lg p-5 space-y-3">
                <h4 className="text-lg font-bold text-cyan-900">{businessType.type}</h4>

                {businessType.question && (
                  <p className="text-sm text-gray-600 italic">❓ {businessType.question}</p>
                )}

                {businessType.retail && (
                  <div className="bg-white border border-gray-200 rounded p-4 mb-2">
                    <p className="font-semibold text-gray-800 mb-2">Розница:</p>
                    {businessType.retail.court && <p className="text-sm text-blue-600 mb-2">Суд: {businessType.retail.court}</p>}

                    {businessType.retail.problems && (
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-orange-700 mb-1">Проблемы:</p>
                        <ul className="list-disc list-inside text-xs text-gray-600">
                          {businessType.retail.problems.map((prob, pIdx) => (
                            <li key={pIdx}>{prob}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {businessType.retail.services && (
                      <div>
                        <p className="text-xs font-semibold text-green-700 mb-1">Услуги:</p>
                        <ul className="list-disc list-inside text-xs text-gray-600">
                          {businessType.retail.services.map((serv, sIdx) => (
                            <li key={sIdx}>{serv}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {businessType.wholesale && (
                  <div className="bg-white border border-gray-200 rounded p-4 mb-2">
                    <p className="font-semibold text-gray-800 mb-2">Опт:</p>
                    {businessType.wholesale.court && <p className="text-sm text-blue-600 mb-2">Суд: {businessType.wholesale.court}</p>}

                    {businessType.wholesale.problems && (
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-orange-700 mb-1">Проблемы:</p>
                        <ul className="list-disc list-inside text-xs text-gray-600">
                          {businessType.wholesale.problems.map((prob, pIdx) => (
                            <li key={pIdx}>{prob}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {businessType.wholesale.services && (
                      <div>
                        <p className="text-xs font-semibold text-green-700 mb-1">Услуги:</p>
                        <ul className="list-disc list-inside text-xs text-gray-600">
                          {businessType.wholesale.services.map((serv, sIdx) => (
                            <li key={sIdx}>{serv}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {businessType.for_legal_entities && (
                  <div className="bg-white border border-gray-200 rounded p-4 mb-2">
                    <p className="font-semibold text-gray-800 mb-2">Для юр. лиц:</p>
                    {businessType.for_legal_entities.court && <p className="text-sm text-blue-600 mb-2">Суд: {businessType.for_legal_entities.court}</p>}

                    {businessType.for_legal_entities.problems && (
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-orange-700 mb-1">Проблемы:</p>
                        <ul className="list-disc list-inside text-xs text-gray-600">
                          {businessType.for_legal_entities.problems.map((prob, pIdx) => (
                            <li key={pIdx}>{prob}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {businessType.for_legal_entities.services && (
                      <div>
                        <p className="text-xs font-semibold text-green-700 mb-1">Услуги:</p>
                        <ul className="list-disc list-inside text-xs text-gray-600">
                          {businessType.for_legal_entities.services.map((serv, sIdx) => (
                            <li key={sIdx}>{serv}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {businessType.for_individuals && (
                  <div className="bg-white border border-gray-200 rounded p-4 mb-2">
                    <p className="font-semibold text-gray-800 mb-2">Для физ. лиц:</p>
                    {businessType.for_individuals.court && <p className="text-sm text-blue-600 mb-2">Суд: {businessType.for_individuals.court}</p>}

                    {businessType.for_individuals.problems && (
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-orange-700 mb-1">Проблемы:</p>
                        <ul className="list-disc list-inside text-xs text-gray-600">
                          {businessType.for_individuals.problems.map((prob, pIdx) => (
                            <li key={pIdx}>{prob}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {businessType.for_individuals.services && (
                      <div>
                        <p className="text-xs font-semibold text-green-700 mb-1">Услуги:</p>
                        <ul className="list-disc list-inside text-xs text-gray-600">
                          {businessType.for_individuals.services.map((serv, sIdx) => (
                            <li key={sIdx}>{serv}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {businessType.problems && Array.isArray(businessType.problems) && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <p className="text-sm font-semibold text-yellow-800 mb-2">Универсальные проблемы:</p>
                    {businessType.problems.map((prob, pIdx) => (
                      <div key={pIdx} className="mb-2 last:mb-0">
                        {prob.area && <p className="font-semibold text-gray-800 text-sm">{prob.area}:</p>}
                        {prob.issues && <p className="text-xs text-gray-600">{prob.issues}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {businessType.specific_problems && (
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <p className="text-sm font-semibold text-red-800 mb-1">Специфические проблемы:</p>
                    <ul className="list-disc list-inside text-sm text-gray-600">
                      {businessType.specific_problems.map((prob, pIdx) => (
                        <li key={pIdx}>{prob}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {businessType.our_advantage && (
                  <p className="text-sm text-green-600 bg-green-50 p-2 rounded font-medium">✅ {businessType.our_advantage}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Argumentation (Module 11) */}
        {content.argumentation && (
          <div className="bg-purple-50 border-2 border-purple-400 rounded-lg p-5">
            <p className="text-sm font-semibold text-purple-800 mb-1">💼 Аргументация:</p>
            {content.argumentation.use_case_list && <p className="text-gray-700 mb-1">{content.argumentation.use_case_list}</p>}
            {content.argumentation.how && <p className="text-sm text-gray-600 mb-1">Как: {content.argumentation.how}</p>}
            {content.argumentation.effect && <p className="text-sm text-green-600 font-medium">→ {content.argumentation.effect}</p>}
          </div>
        )}

        {/* Errors (Module 13) */}
        {content.errors && Array.isArray(content.errors) && (
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Критические ошибки:</h3>
            {content.errors.map((error, idx) => (
              <div key={idx} className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 space-y-2">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center font-bold mr-4 text-lg">
                    {error.number}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-red-900 mb-1">{error.error}</h4>
                    {error.impact && <p className="text-sm text-red-700 mb-2">💥 Последствия: {error.impact}</p>}
                    {error.fix && (
                      <div className="bg-green-50 border border-green-200 rounded p-3">
                        <p className="text-sm font-semibold text-green-800 mb-1">✅ Как исправить:</p>
                        <p className="text-gray-700">{error.fix}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Self check (Module 13) */}
        {content.self_check && (
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-400 rounded-lg p-5">
            <p className="text-sm font-semibold text-orange-800 mb-1">📝 Самопроверка:</p>
            <p className="text-gray-800 font-medium">{content.self_check}</p>
          </div>
        )}

        {/*Render sections if present */}
        {
          moduleSections && Array.isArray(moduleSections) && moduleSections.map((section, sectionIdx) => (
            <div key={sectionIdx} className="bg-gray-50 rounded-lg p-5 space-y-4">
              {section.title && (
                <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-blue-500 pb-2">
                  {section.title}
                </h3>
              )}

              {/* Content array */}
              {section.content && Array.isArray(section.content) && (
                <div className="space-y-2">
                  {section.content.map((item, idx) => (
                    <p key={idx} className="text-gray-700 leading-relaxed">{item}</p>
                  ))}
                </div>
              )}

              {/* Key message */}
              {section.key_message && (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <p className="text-sm font-semibold text-blue-800">🔑 Ключевое сообщение:</p>
                  <p className="text-gray-700 mt-1">{section.key_message}</p>
                </div>
              )}

              {/* Achievements */}
              {section.achievements && Array.isArray(section.achievements) && (
                <div className="bg-green-50 border border-green-200 rounded p-4">
                  <p className="text-sm font-semibold text-green-800 mb-3">✨ Достижения:</p>
                  <div className="space-y-2">
                    {section.achievements.map((achievement, idx) => (
                      <div key={idx} className="bg-white rounded p-3">
                        <p className="font-semibold text-gray-800">{achievement.fact}</p>
                        <p className="text-sm text-gray-600">{achievement.details}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Specialization */}
              {section.specialization && Array.isArray(section.specialization) && (
                <div className="bg-purple-50 border border-purple-200 rounded p-4">
                  <p className="text-sm font-semibold text-purple-800 mb-2">🎯 Специализация:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {section.specialization.map((spec, idx) => (
                      <li key={idx}>{spec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Departments */}
              {section.departments && Array.isArray(section.departments) && (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-800">🏢 Отделы:</p>
                  {section.departments.map((dept, idx) => (
                    <div key={idx} className="bg-white border border-gray-200 rounded p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">{dept.name}</h4>
                      {dept.focus && (
                        <p className="text-sm text-gray-700 mb-1">
                          <span className="font-medium">Фокус:</span> {dept.focus}
                        </p>
                      )}
                      {dept.metrics && (
                        <p className="text-sm text-gray-700 mb-1">
                          <span className="font-medium">Метрики:</span> {dept.metrics}
                        </p>
                      )}
                      {dept.note && (
                        <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded mt-2">
                          💡 {dept.note}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Transfer criteria */}
              {section.transfer_criteria && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                  <p className="text-sm font-semibold text-yellow-800 mb-3">📋 Критерии передачи:</p>

                  {section.transfer_criteria.required && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Обязательно:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                        {section.transfer_criteria.required.map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {section.transfer_criteria.what_to_clarify && (
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-1">Что уточнить:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                        {section.transfer_criteria.what_to_clarify.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Warning */}
              {section.warning && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <p className="text-sm font-semibold text-red-800">⚠️ {section.warning}</p>
                </div>
              )}

              {/* Distribution */}
              {section.distribution && (
                <div className="bg-indigo-50 border border-indigo-200 rounded p-4">
                  <p className="text-sm text-gray-700">{section.distribution}</p>
                </div>
              )}

              {/* Transfer process */}
              {section.transfer_process && (
                <div className="bg-teal-50 border border-teal-200 rounded p-4">
                  <p className="text-sm text-gray-700">{section.transfer_process}</p>
                </div>
              )}
            </div>
          ))
        }

        {/* Render steps if present */}
        {
          moduleSteps && Array.isArray(moduleSteps) && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Шаги:</h3>
              {moduleSteps.map((step, stepIdx) => (
                <div key={stepIdx} className="flex items-start bg-white border-2 border-blue-200 rounded-lg p-5 space-y-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold mr-4 text-xl">
                    {step.number || stepIdx + 1}
                  </div>
                  <div className="flex-1 space-y-3">
                    {/* Step name/title */}
                    {(step.step || step.title) && (
                      <h4 className="font-bold text-gray-900 text-lg">{step.step || step.title}</h4>
                    )}

                    {/* Description */}
                    {step.description && (
                      <p className="text-gray-700">{step.description}</p>
                    )}

                    {/* How (Module 12) */}
                    {step.how && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <p className="text-sm font-semibold text-blue-800 mb-1">Как:</p>
                        {Array.isArray(step.how) ? (
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                            {step.how.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-700">{step.how}</p>
                        )}
                      </div>
                    )}

                    {/* Why (Module 12) */}
                    {step.why && (
                      <div className="bg-purple-50 border border-purple-200 rounded p-3">
                        <p className="text-sm font-semibold text-purple-800 mb-1">Почему:</p>
                        <p className="text-sm text-gray-700">{step.why}</p>
                      </div>
                    )}

                    {/* Question (Module 12) */}
                    {step.question && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                        <p className="text-sm text-yellow-800 italic">❓ {step.question}</p>
                      </div>
                    )}

                    {/* Action (Module 12) */}
                    {step.action && (
                      <div className="bg-green-50 border border-green-200 rounded p-3">
                        <p className="text-sm font-semibold text-green-800 mb-1">Действие:</p>
                        <p className="text-sm text-gray-700">{step.action}</p>
                      </div>
                    )}

                    {/* Effect (Module 12) */}
                    {step.effect && (
                      <div className="bg-green-50 border border-green-200 rounded p-3">
                        <p className="text-sm font-semibold text-green-800 mb-1">Результат:</p>
                        <p className="text-sm text-gray-700">{step.effect}</p>
                      </div>
                    )}

                    {/* Goal */}
                    {step.goal && (
                      <div className="bg-purple-50 border border-purple-200 rounded p-3">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-purple-800">🎯 Цель:</span> {step.goal}
                        </p>
                      </div>
                    )}

                    {/* Analogy (Module 12) */}
                    {step.analogy && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded p-3">
                        <p className="text-sm text-indigo-700 italic">💡 Аналогия: {step.analogy}</p>
                      </div>
                    )}

                    {/* Principle (Module 12) */}
                    {step.principle && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <p className="text-sm font-semibold text-blue-800 mb-1">Принцип:</p>
                        <p className="text-sm text-gray-700">{step.principle}</p>
                      </div>
                    )}

                    {/* Rule (Module 12) */}
                    {step.rule && (
                      <div className="bg-red-50 border border-red-200 rounded p-3">
                        <p className="text-sm font-semibold text-red-800 mb-1">Правило:</p>
                        <p className="text-sm text-gray-700">{step.rule}</p>
                      </div>
                    )}

                    {/* Fact (Module 12) */}
                    {step.fact && (
                      <div className="bg-amber-50 border border-amber-200 rounded p-3">
                        <p className="text-sm text-amber-800">📌 {step.fact}</p>
                      </div>
                    )}

                    {/* Insight (Module 12) */}
                    {step.insight && (
                      <div className="bg-purple-50 border border-purple-200 rounded p-3">
                        <p className="text-sm text-purple-700 font-medium">💎 {step.insight}</p>
                      </div>
                    )}

                    {/* Actions */}
                    {step.actions && Array.isArray(step.actions) && (
                      <ul className="list-disc list-inside space-y-1 text-gray-700 mb-2">
                        {step.actions.map((action, idx) => (
                          <li key={idx}>{action}</li>
                        ))}
                      </ul>
                    )}

                    {/* Warning */}
                    {step.warning && (
                      <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-2">
                        <p className="text-sm font-semibold text-orange-800">⚠️ {step.warning}</p>
                      </div>
                    )}

                    {/* Examples */}
                    {step.examples && Array.isArray(step.examples) && (
                      <div className="bg-green-50 border border-green-200 rounded p-3 mb-2">
                        <p className="text-sm font-semibold text-green-800 mb-2">Примеры:</p>
                        {step.examples.map((example, idx) => (
                          <div key={idx} className="text-sm text-gray-700 mb-1">
                            {example.activity && (
                              <span className="font-medium">{example.activity}:</span>
                            )}
                            {example.problems && (
                              <span> {example.problems}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* What to find */}
                    {step.what_to_find && Array.isArray(step.what_to_find) && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-2">
                        <p className="text-sm font-semibold text-blue-800 mb-1">Что искать:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                          {step.what_to_find.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </div >
    );
  }

  // Fallback: try to render as JSON
  return (
    <div className="bg-gray-50 rounded p-4">
      <pre className="text-xs text-gray-700 overflow-auto">
        {JSON.stringify(content, null, 2)}
      </pre>
    </div>
  );
};

MaterialContent.propTypes = {
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
};

export default MaterialContent;
